/**
 * A agenda desenhada como grade de cartões, um por contato.
 *
 * Papel: mostra a categoria, o nome, os telefones, o e-mail, o endereço e a
 * observação de cada contato, mais os botões de editar e remover para quem
 * recebe `onEditar` e `onRemover`.
 *
 * Como usar: quem monta a lista passa `carregando` enquanto a consulta não
 * respondeu e `mensagemVazia` com o texto que cabe ao caso — não achar nada
 * numa busca e não haver contato nenhum são situações diferentes, e um texto
 * único faria a busca sem resultado parecer defeito. `onNovoContato` só é
 * passado quando a agenda está de fato vazia (sem busca ativa) e quem está na
 * tela pode cadastrar; nesse caso o bloco de vazio ganha o próprio botão de
 * criar, ao lado do que já existe no cabeçalho da página. `onEditar` e
 * `onRemover` só deverão ser passados a quem alcança `editar` no módulo: sem
 * eles o cartão não desenha botão nenhum, e quem só tem `ver` continua lendo
 * no cartão tudo o que o formulário mostraria.
 *
 * Cada telefone ocupa uma linha própria, na ordem de `telefones`, escrita como
 * `número | falar com` — o `| falar com` só quando `falarCom` existe. É no
 * cartão, e só nele, que quem tem `ver` descobre o segundo número e com quem
 * falar em cada um; a busca também alcança esses dois dados, e um cartão que
 * os escondesse apareceria como resultado sem mostrar o motivo. Um contato sem
 * telefone nenhum mostra uma linha só, com o mesmo travessão que um e-mail ou
 * um endereço ausente.
 */
import { type ContatoMailing } from '@concierge/shared';

import { formatarTelefone } from '@/shared/lib/formato';
import { Button } from '@/shared/ui/button';
import { Skeleton } from '@/shared/ui/skeleton';
import { Vazio } from '@/shared/ui/vazio';
import { IconeEmail, IconeEndereco, IconeTelefone } from './icones-de-contato';

interface ListaContatosProps {
  /** Os contatos da página, ou `undefined` enquanto a consulta não respondeu. */
  contatos: ContatoMailing[] | undefined;
  carregando: boolean;
  mensagemVazia: string;
  /**
   * Abre o formulário de criação a partir do botão do bloco de vazio.
   * Ausente quando a busca está ativa ou quem está na tela não pode
   * cadastrar — casos em que o bloco de vazio não ganha botão nenhum.
   */
  onNovoContato?: () => void;
  /** Abre o contato no formulário; ausente, o cartão não desenha "editar". */
  onEditar?: (contato: ContatoMailing) => void;
  /** Pede a exclusão do contato; ausente, o cartão não desenha "remover". */
  onRemover?: (contato: ContatoMailing) => void;
}

/** Quantos cartões vazios desenhar enquanto a primeira página carrega. */
const CARTOES_DE_ESPERA = 6;

/**
 * As colunas fluidas do cartão de contato: nunca menos de 260px cada, e uma
 * só coluna a 560px ou menos — a única grade de cartões que não usa as quatro
 * colunas fixas de `GradeDeCartoes`.
 */
const GRADE_DE_CONTATOS =
  'grid grid-cols-[repeat(auto-fill,minmax(260px,1fr))] gap-4 max-[561px]:grid-cols-1';

/**
 * Escreve a linha de um telefone no cartão.
 *
 * @param telefone - Um item de `telefones`, com o número só em dígitos.
 * @returns O número formatado por `formatarTelefone`, seguido de ` | ` e de
 *   `falarCom` quando existe alguém com quem falar nele.
 */
function linhaDoTelefone(telefone: ContatoMailing['telefones'][number]): string {
  const numero = formatarTelefone(telefone.numero);
  return telefone.falarCom === null ? numero : `${numero} | ${telefone.falarCom}`;
}

/**
 * A grade de contatos, com os três estados que a consulta produz.
 *
 * @param contatos - Os contatos da página atual.
 * @param carregando - Verdadeiro enquanto a primeira resposta não chegou.
 * @param mensagemVazia - O que dizer quando a página não tem nenhum contato.
 * @param onNovoContato - Chamado ao clicar no botão de criar dentro do bloco
 *   de vazio; sem ele, o bloco não desenha botão nenhum.
 * @param onEditar - Chamado com o contato do cartão em que se clicou; ausente,
 *   nenhum cartão mostra "editar".
 * @param onRemover - Chamado com o contato a excluir; a confirmação é de quem
 *   monta a lista, e não daqui. Ausente, nenhum cartão mostra "remover", e sem
 *   os dois a faixa de ações do cartão some inteira.
 * @returns A grade, o esqueleto de carregamento ou o aviso de lista vazia.
 */
export function ListaContatos({
  contatos,
  carregando,
  mensagemVazia,
  onNovoContato,
  onEditar,
  onRemover,
}: ListaContatosProps) {
  if (carregando) {
    return (
      <div className={GRADE_DE_CONTATOS}>
        {Array.from({ length: CARTOES_DE_ESPERA }, (_, indice) => (
          <Skeleton key={indice} className="h-48 rounded-md" />
        ))}
      </div>
    );
  }

  if (contatos === undefined || contatos.length === 0) {
    return (
      <Vazio
        mensagem={mensagemVazia}
        rotuloAcao={onNovoContato !== undefined ? '+ Novo contato' : undefined}
        onAcao={onNovoContato}
        varianteAcao="default"
      />
    );
  }

  return (
    <ul className={`${GRADE_DE_CONTATOS} list-none p-0`}>
      {contatos.map((contato) => (
        <li
          key={contato.id}
          className="flex flex-col gap-[7px] rounded-md border border-linha-forte bg-superficie p-[18px_20px] transition-[border-color,transform] duration-200 ease-in-out hover:-translate-y-0.5 hover:border-brass"
        >
          <span className="font-mono text-[10px] tracking-[0.08em] text-brass uppercase">
            {contato.categoria}
          </span>

          <h2 className="mb-0.5 text-[15.5px] leading-[1.3] font-semibold break-words text-foreground">
            {contato.nome}
          </h2>

          {contato.telefones.length === 0 ? (
            <div className="flex items-start gap-2 text-[13px] break-words text-texto-medio">
              <IconeTelefone className="mt-0.5 size-3.5 shrink-0 text-texto-fraco" />
              <span>—</span>
            </div>
          ) : (
            contato.telefones.map((telefone) => (
              <div
                key={telefone.numero}
                className="flex items-start gap-2 text-[13px] break-words text-texto-medio"
              >
                <IconeTelefone className="mt-0.5 size-3.5 shrink-0 text-texto-fraco" />
                <span>{linhaDoTelefone(telefone)}</span>
              </div>
            ))
          )}

          <div className="flex items-start gap-2 text-[13px] break-words text-texto-medio">
            <IconeEmail className="mt-0.5 size-3.5 shrink-0 text-texto-fraco" />
            <span>{contato.email ?? '—'}</span>
          </div>

          <div className="flex items-start gap-2 text-[13px] break-words text-texto-medio">
            <IconeEndereco className="mt-0.5 size-3.5 shrink-0 text-texto-fraco" />
            <span>{contato.endereco ?? '—'}</span>
          </div>

          {contato.observacao !== null && (
            <p className="mt-0.5 border-t border-linha pt-2 text-xs [overflow-wrap:anywhere] break-words text-texto-fraco">
              {contato.observacao}
            </p>
          )}

          {(onEditar !== undefined || onRemover !== undefined) && (
            <div className="mt-2.5 flex gap-2 border-t border-linha pt-2.5">
              {onEditar !== undefined && (
                <Button variant="outline" size="sm" onClick={() => onEditar(contato)}>
                  editar
                </Button>
              )}
              {onRemover !== undefined && (
                <Button variant="destructive" onClick={() => onRemover(contato)}>
                  remover
                </Button>
              )}
            </div>
          )}
        </li>
      ))}
    </ul>
  );
}
